import { useMemo } from 'react';
import moment from 'moment-timezone';
import { useAuth } from '../firebase/useAuth';
import { useForecast } from '../contexts/ForecastProvider';
import { useMetadata } from '../contexts/MetadataProvider';

// Sum every wk bucket in the row for a single JLPT level.
const sumWkRow = (byWk, jlpt) => {
  const row = byWk?.[jlpt];
  if (!row) return 0;
  return Object.values(row).reduce((sum, n) => sum + (n || 0), 0);
};

// Sum the row for a single JLPT level, restricted to wk buckets within [wkMin, wkMax].
const sumWkRowFiltered = (byWk, jlpt, wkMin, wkMax) => {
  const row = byWk?.[jlpt];
  if (!row) return 0;
  let total = 0;
  for (const [wk, count] of Object.entries(row)) {
    const w = Number(wk);
    if (wkMin != null && w < wkMin) continue;
    if (wkMax != null && w > wkMax) continue;
    total += count || 0;
  }
  return total;
};

export const useDashboardStats = () => {
  const { userData, subscriptionStatus } = useAuth();
  const { reviewForecasts, sentenceCounts, sentenceCountsByWk, wordCounts, loading: forecastLoading } = useForecast();
  const {
    learnedTodayData,
    countsByLevel,
    countsBySrs,
    reviewAccuracy,
    loading: metadataLoading
  } = useMetadata();

  const loading = forecastLoading || metadataLoading;


  // Process metadata stats into structured format
  const sentencesStats = useMemo(() => {
    if (!countsByLevel || !countsBySrs || !reviewAccuracy) return null;

    // Per-JLPT learned counts are derived from the wk histogram so we keep one source of truth.
    const sentenceCountsPerLevel = {};
    let totalLearnedSentences = 0;
    for (const jlpt of [1, 2, 3, 4, 5]) {
      const learnedAtLevel = sumWkRow(countsByLevel.sentences_by_wk, jlpt);
      sentenceCountsPerLevel[jlpt] = learnedAtLevel;
      totalLearnedSentences += learnedAtLevel;
    }

    const rawSentenceSrs = countsBySrs.sentences || {};
    const sentenceSrsStage = { ...rawSentenceSrs };
    // Keep skipped separate - don't combine with stage 7

    const sentenceAccuracy = reviewAccuracy.sentences || {};
    const sentenceTotal = (sentenceAccuracy.correct || 0) + (sentenceAccuracy.wrong || 0);

    return {
      byLevel: sentenceCountsPerLevel,
      totalLearned: totalLearnedSentences,
      bySrsStage: sentenceSrsStage,
      accuracy: {
        correct: sentenceAccuracy.correct || 0,
        total: sentenceTotal,
        ratio: sentenceTotal > 0 ? (sentenceAccuracy.correct || 0) / sentenceTotal : 0,
      },
    };
  }, [countsByLevel, countsBySrs, reviewAccuracy]);

  const wordsStats = useMemo(() => {
    if (!countsByLevel || !countsBySrs || !reviewAccuracy) return null;

    const wordCounts = countsByLevel.words || {};
    const totalLearnedWords = Object.values(wordCounts).reduce((sum, count) => sum + count, 0);
    
    const rawWordSrs = countsBySrs.words || {};
    const wordSrsStage = { ...rawWordSrs };
    // Keep skipped separate - don't combine with stage 7
    
    const wordAccuracy = reviewAccuracy.words || {};
    const wordTotal = (wordAccuracy.correct || 0) + (wordAccuracy.wrong || 0);

    return {
      byLevel: wordCounts,
      totalLearned: totalLearnedWords,
      bySrsStage: wordSrsStage,
      accuracy: {
        correct: wordAccuracy.correct || 0,
        total: wordTotal,
        ratio: wordTotal > 0 ? (wordAccuracy.correct || 0) / wordTotal : 0,
      },
    };
  }, [countsByLevel, countsBySrs, reviewAccuracy]);


  const processedStats = useMemo(() => {
    if (loading || !userData || !sentencesStats || !wordsStats || subscriptionStatus?.loading || learnedTodayData === undefined) {
      return null;
    }

    const userTimezone = userData.settings.timezone;
    const isHourlyEnabled = userData.settings.review_frequency === 'hour';
    const isSubscribed = subscriptionStatus?.isSubscribed || false;
    const todayDate = moment().tz(userTimezone).format('YYYY-MM-DD');
    const todayKey = moment().tz(userTimezone).format('YYYY-MM-DD');
    const currentHour = moment().tz(userTimezone).hour();

    const getReviewableNowCount = (forecast) => {
      if (isHourlyEnabled) {
        const todayHourlyData = forecast[`${todayKey}:hourly`];
        if (!todayHourlyData) return 0;

        let count = 0;
        for (let hour = 0; hour <= currentHour; hour++) {
          count += todayHourlyData[hour] || 0;
        }
        return count;
      } else {
        return forecast[todayKey] || 0;
      }
    };

    const reviewableNowSentences = getReviewableNowCount(reviewForecasts.sentences);
    const reviewableNowWords = getReviewableNowCount(reviewForecasts.words);

    // Calculate toLearnToday using real-time learnedTodayData
    const learnPerDay = userData.settings.sentences_per_day;
    const learnedToday = learnedTodayData?.[todayDate] || 0;
    const remainingToLearn = learnPerDay - learnedToday;

    const levelsToLearn = userData.settings.levels_to_learn_from && isSubscribed ? userData.settings.levels_to_learn_from : [5];

    const wkMin = userData.settings.wk_level_min != null ? Number(userData.settings.wk_level_min) : null;
    const wkMax = userData.settings.wk_level_max != null ? Number(userData.settings.wk_level_max) : null;
    const userSentencesByWk = countsByLevel?.sentences_by_wk || {};

    // Compute unlearned counts two ways: ignoring the user's wk cap (for the upgrade-prompt
    // check) and applying it (for the actual "lessons available" number).
    let totalUnlearned = 0;
    let totalUnlearnedInRange = 0;
    levelsToLearn.forEach((lvl) => {
      const totalAtLevel = sumWkRow(sentenceCountsByWk, lvl);
      const learnedAtLevel = sumWkRow(userSentencesByWk, lvl);
      totalUnlearned += Math.max(0, totalAtLevel - learnedAtLevel);

      const totalInRangeAtLevel = sumWkRowFiltered(sentenceCountsByWk, lvl, wkMin, wkMax);
      const learnedInRangeAtLevel = sumWkRowFiltered(userSentencesByWk, lvl, wkMin, wkMax);
      totalUnlearnedInRange += Math.max(0, totalInRangeAtLevel - learnedInRangeAtLevel);
    });

    const toLearnToday = Math.min(Math.max(0, remainingToLearn), totalUnlearnedInRange);

    const FREE_LEVELS = [0, 5];
    const totalFree = FREE_LEVELS.reduce((sum, lvl) => sum + (sentenceCounts[lvl] || 0), 0);
    const learnedFree = FREE_LEVELS.reduce((sum, lvl) => sum + (sentencesStats.byLevel[lvl] || 0), 0);
    const totalAll = Object.values(sentenceCounts).reduce((sum, c) => sum + c, 0);
    const showLearnUpgradePrompt = !isSubscribed && toLearnToday === 0 && learnedFree >= totalFree && sentencesStats.totalLearned < totalAll;

    // WK-cap hint: the user has unlearned items in their selected JLPT levels, but every one
    // of them sits outside their [wkMin, wkMax] window. Suppressed when the upgrade prompt
    // is taking over the card.
    const hasWkFilter = wkMin != null || wkMax != null;
    const showWkCapHint =
      hasWkFilter &&
      !showLearnUpgradePrompt &&
      remainingToLearn > 0 &&
      totalUnlearnedInRange === 0 &&
      totalUnlearned > 0;

    return {
      totalCounts: {
        totalSentences: sentenceCounts,
        totalWords: wordCounts,
      },
      sentencesStats: {
        ...sentencesStats,
        reviewableNow: reviewableNowSentences,
        reviewForecast: reviewForecasts.sentences,
        toLearnToday,
        showLearnUpgradePrompt,
        showWkCapHint,
      },
      wordsStats: {
        ...wordsStats,
        reviewableNow: reviewableNowWords,
        reviewForecast: reviewForecasts.words,
      },
    };
  }, [
    sentencesStats,
    wordsStats,
    reviewForecasts,
    userData,
    sentenceCounts,
    sentenceCountsByWk,
    countsByLevel,
    wordCounts,
    learnedTodayData,
    subscriptionStatus,
    loading,
  ]);

  return {
    stats: processedStats,
    loading,
  };
};
